import { flattenMoves } from '@core/models/entities/implementations/route-action-occupants';
import {
  UserRouteActionV1Model,
  getHandledJobUids
} from '@core/models/entities/implementations/user-route-action-v1';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';

import { actionFinish, actionStart, routeStops } from './route-timing';

/** A run of a route's stops between two loadings at the depot, numbered from 1. */
export interface Tour {
  index: number;
  actionUids: string[];
  /** The first action's scheduled start, its drive included. */
  start: string;
  /** The last action's finish. */
  end: string;
}

function movesOf(action: UserRouteActionV1Model) {
  return flattenMoves(action.movements ?? []);
}

function onlyLoads(action: UserRouteActionV1Model): boolean {
  const moves = movesOf(action);
  return moves.length > 0 && moves.every((move) => move.kind === 'load');
}

function unloads(action: UserRouteActionV1Model): boolean {
  return movesOf(action).some((move) => move.kind === 'unload');
}

/**
 * Judged on movements, as unloading is: the stop that unloaded is never a passage, so the tour it
 * ends keeps at least that stop.
 */
function isPassage(action: UserRouteActionV1Model): boolean {
  return (action.movements ?? []).length === 0;
}

/**
 * Splits a route's stops into tours. A tour starts at a stop that only loads, once the tour
 * before it has unloaded; the first tour starts at the first stop, so a preloaded route that
 * only delivers is one tour. Actions moving nothing right before such a loading stop are the
 * depot passage that tour begins with, not the end of the previous one.
 */
export function toursOf(route: UserRouteV1Model): Tour[] {
  const groups: UserRouteActionV1Model[][] = [];
  let current: UserRouteActionV1Model[] = [];
  let unloaded = false;
  for (const action of routeStops(route)) {
    if (unloaded && onlyLoads(action)) {
      let passage = current.length;
      while (passage > 0 && isPassage(current[passage - 1])) passage--;
      groups.push(current.slice(0, passage));
      current = current.slice(passage);
      unloaded = false;
    }
    current.push(action);
    unloaded ||= unloads(action);
  }
  if (current.length > 0) groups.push(current);

  return groups.map((actions, i) => ({
    index: i + 1,
    actionUids: actions.map((action) => action.uid),
    start: actionStart(actions[0]).toISOString(),
    end: actionFinish(actions[actions.length - 1]).toISOString()
  }));
}

/**
 * The first job the tour serves that the route also serves outside it, with the tour that does.
 * DL refuses to move such a tour, since the job's operations would end up on two routes.
 */
export function tourMoveBlocker(
  route: UserRouteV1Model,
  tour: Tour,
  tours: Tour[] = toursOf(route)
): { jobUid: string; tourIndex: number } | null {
  const inside = new Set(tour.actionUids);
  const byUid = new Map(route.actions.map((action) => [action.uid, action]));
  for (const uid of tour.actionUids) {
    const action = byUid.get(uid);
    if (!action) continue;
    for (const jobUid of getHandledJobUids(action)) {
      const elsewhere = route.actions.find(
        (other) => !inside.has(other.uid) && getHandledJobUids(other).includes(jobUid)
      );
      const other = elsewhere && tours.find((t) => t.actionUids.includes(elsewhere.uid));
      if (other) return { jobUid, tourIndex: other.index };
    }
  }
  return null;
}
